import { TUser } from "@/api/entities/user.entity";
import { TCreateUserReqDto, TCreateUserResDto } from "@/api/services/admin/rso/user/dtos/create-user.dto";
import { TDeleteUserResDto } from "@/api/services/admin/rso/user/dtos/delete-user.dto";
import { TGetUserListResDto } from "@/api/services/admin/rso/user/dtos/get-user-list.dto";
import { TGetUserResDto } from "@/api/services/admin/rso/user/dtos/get-user.dto";
import { TResendInvitationResDto } from "@/api/services/admin/rso/user/dtos/resend-invitation.dto";
import { TResetPasswordResDto } from "@/api/services/admin/rso/user/dtos/reset-password.dto";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { TUpdatePartnerUserForm } from "@/features/rso/types/updatePartnerUserForm";
import { TApiRes } from "@/types/api";
import { clientApi } from "@/utils/api";
import { isNumber } from "@/utils/number";
import { createUrlWithParams } from "@/utils/url";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

export const createRSOUserApi = async (rsoId: string, payload: TCreateUserReqDto): Promise<TCreateUserResDto> => {
  const rs = await clientApi.post<TCreateUserResDto>(`/rsos/${rsoId}/users`, payload);
  return rs.data;
};

export const deleteRSOUserApi = async (rsoId: number, userIds: number[]): Promise<TDeleteUserResDto> => {
  const rs = await clientApi.delete<TDeleteUserResDto>(`/rsos/${rsoId}/users`, {
    data: {
      ids: userIds,
    },
  });
  return rs.data;
};

export const fetchRSOUserApi = async (rsoId: string, userId: string): Promise<TGetUserResDto> => {
  const rs = await clientApi.get<TGetUserResDto>(`/rsos/${rsoId}/users/${userId}`);
  return rs.data;
};

export const useRSOUser = () => {
  const { id, userId } = useParams();

  return useQuery({
    queryKey: [QUERY_KEYS.RSO.FETCH_USER_DETAIL, id, userId],
    queryFn: () => (id && userId ? fetchRSOUserApi(id, userId) : undefined),
    enabled: Boolean(id && isNumber(id) && userId && isNumber(userId)),
  });
};

export const fetchRSOUserListDetailApi = async (rsoId: string): Promise<TGetUserListResDto> => {
  const url = createUrlWithParams(`/rsos/${rsoId}/users`, { sorts: ["id|ASC"] });
  const rs = await clientApi.get<TGetUserListResDto>(url);
  return rs.data;
};

export const useRSOUserListDetail = () => {
  const { id } = useParams();

  return useQuery({
    queryKey: [QUERY_KEYS.RSO.FETCH_USER_LIST, id],
    queryFn: () => (id ? fetchRSOUserListDetailApi(id) : undefined),
    enabled: Boolean(id),
  });
};

export const updateRSOUserApi = async (rsoId: string, userId: string, payload: TUpdatePartnerUserForm): Promise<TApiRes<TUser>> => {
  try {
    const { ...rest } = payload;

    const { data } = await clientApi.put<TApiRes<TUser>>(`/rsos/${rsoId}/users/${userId}`, {
      ...rest,
      isUser: Boolean(payload.isUser),
    });
    return Promise.resolve(data);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const resendInvitationApi = async (rsoId: string, userId: string): Promise<TResendInvitationResDto> => {
  const rs = await clientApi.post<TResendInvitationResDto>(`/rsos/${rsoId}/users/${userId}/resend-invitation`);
  return rs.data;
};

export const resetPasswordApi = async (rsoId: string, userId: string): Promise<TResetPasswordResDto> => {
  const rs = await clientApi.post<TResetPasswordResDto>(`/rsos/${rsoId}/users/${userId}/resend-reset-password`);
  return rs.data;
};
